import { MagnifyingGlassPlusIcon } from "../../../../automationStudioIcons";
import { useState, useEffect, useMemo, useRef } from "react";
import { resolveScreenshotUrl, refreshScreenshotUrl } from "../../../../../../utils/screenshotUtils";
import ImagePreviewModal from "../../../../../Modal/ImagePreviewModal";
import StepAgentResultTrace from '../agentResult/StepAgentResultTrace';
import { mapStepWiseDynamicResultsToTraceTurns } from '../agentResult/mapStepWiseDynamicResultsToTraceTurns';
import { EXECUTION_STATUS } from '../../../../../AutomationStudio/hooks/useReplayExecution';
import useAutomationTextFormatter from '../../../../../AutomationStudio/hooks/useAutomationTextFormatter';
import AutomationText from "../../../shared/AutomationText";
import ToolOutput from "../../../shared/ToolOutput";
import ScreenshotPlaceholder from "../../../shared/ScreenshotPlaceholder";
import styles from "../../../../../../styles/automationStudio/automationStudio.module.scss";

// Tools that don't produce screenshots — output renders via <ToolOutput /> below.
const NO_SCREENSHOT_TOOLS = new Set([
    'browser_network_request',
    'browser_network_requests',
    'browser_console_messages',
    'browser_evaluate',
]);

/**
 * PreviewTab - Screenshot and execution metadata display
 * Merges step data with replay execution data (screenshot, status) when available
 */

export default function PreviewTab({ step, instructions = [], replayExecutionResults = null, replayProgressMap = {}, isReplayFlow = false }) {
    const { format } = useAutomationTextFormatter();
    const [screenshotModalOpen, setScreenshotModalOpen] = useState(false);
    const [screenshotUrl, setScreenshotUrl] = useState(null);
    const [screenshotLoading, setScreenshotLoading] = useState(false);
    const [hasImageLoadError, setHasImageLoadError] = useState(false);
    // One-shot guard: refresh a broken screenshot URL once, then fall back to the placeholder.
    const screenshotRetriedRef = useRef(false);

    // Resolve flat step index for selected step (same logic as buildPlanFromInstructions)
    const flatStepIndex = useMemo(() => {
        if (!step?.id || !instructions?.length) return null;
        let idx = 0;
        for (const inst of instructions) {
            const steps = inst.aiSteps || [];
            for (const s of steps) {
                if (s.id === step.id) return idx;
                idx++;
            }
        }
        return null;
    }, [step?.id, instructions]);

    // Merge execution data from replay when available (screenshot, status)
    const stepDetails = flatStepIndex != null && replayExecutionResults?.stepDetails
        ? replayExecutionResults.stepDetails[flatStepIndex]
        : null;
    const replayScreenshot = stepDetails?.screenshot ?? stepDetails?.result?.screenshot ?? stepDetails?.result?.result?.screenshot;
    const replayStatusRaw = flatStepIndex != null ? replayProgressMap[flatStepIndex] : null;

    const replayTraceTurns = useMemo(() => {
        const completion = stepDetails?.result;
        if (!completion || typeof completion !== "object") return undefined;
        const nested = completion.result && typeof completion.result === "object" ? completion.result : null;
        const raw = completion.stepWiseDynamicResults ?? nested?.stepWiseDynamicResults;
        if (!Array.isArray(raw) || raw.length === 0) return undefined;
        const mapped = mapStepWiseDynamicResultsToTraceTurns(raw);
        return mapped.length > 0 ? mapped : undefined;
    }, [stepDetails]);

    // Map EXECUTION_STATUS to step-like status for display
    const effectiveStatus = useMemo(() => {
        if (replayStatusRaw === EXECUTION_STATUS.PASSED) return 'success';
        if (replayStatusRaw === EXECUTION_STATUS.FAILED) return 'failed';
        if (replayStatusRaw === EXECUTION_STATUS.IN_PROGRESS || replayStatusRaw === EXECUTION_STATUS.PENDING) return 'running';
        return step?.status || 'pending';
    }, [replayStatusRaw, step?.status]);

    const toolHasNoScreenshot = NO_SCREENSHOT_TOOLS.has(step?.tool);

    // Extract screenshot path: prefer execution data first, then step data.
    // Force null for non-visual tools so we never fetch or render a screenshot
    // for them, even if the backend mistakenly attached one.
    const replayScreenshotPath = typeof replayScreenshot === 'string'
        ? replayScreenshot
        : (replayScreenshot?.url || replayScreenshot?.path);
    // In replay mode show only THIS replay's screenshot — recorded/step-level keys are stale
    // (from a prior run or the recording) and often 404 on S3, rendering as a broken image.
    const recordedScreenshotPath =
           step?.screenshotPath ||
           step?.screenshot ||
           step?.details?.screenshotPath ||
           step?.extractedData?.screenshotPath ||
           step?.executionGroup?.mainAction?.details?.screenshotPath ||
           null;
    const screenshotPath = toolHasNoScreenshot
        ? null
        : (replayScreenshotPath || (isReplayFlow ? null : recordedScreenshotPath));

    // Fetch and resolve screenshot URL when path changes
    useEffect(() => {
        const fetchScreenshot = async () => {
            if (screenshotPath) {
                setScreenshotLoading(true);
                setHasImageLoadError(false);
                screenshotRetriedRef.current = false;
                try {
                    const { url } = await resolveScreenshotUrl(screenshotPath);
                    setScreenshotUrl(url);
                } catch (error) {
                    setScreenshotUrl(null);
                } finally {
                    setScreenshotLoading(false);
                }
            } else {
                setScreenshotUrl(null);
                setHasImageLoadError(false);
            }
        };

        fetchScreenshot();
    }, [screenshotPath]);

    return (
        <div className={styles.previewTabContainer}>
            {/* Screenshot Section */}
            <div className={styles.screenshotSection}>
                <div className={styles.screenshotHeader}>
                    <span className={`${styles.sectionLabel} ${styles.asTypoSubtitle2}`}>
                        Screenshot
                    </span>
                    {screenshotPath && screenshotUrl && !hasImageLoadError && (
                        <button
                            type="button"
                            onClick={() => setScreenshotModalOpen(true)}
                            title="Enlarge screenshot"
                            className={`${styles.asIconButton} ${styles.enlargeButton}`}
                        >
                            <MagnifyingGlassPlusIcon style={{ width: '16px', height: '16px' }} />
                        </button>
                    )}
                </div>

                {toolHasNoScreenshot ? (
                    <ScreenshotPlaceholder variant="notApplicable" size="panel" />
                ) : screenshotLoading ? (
                    <ScreenshotPlaceholder variant="loading" size="panel" />
                ) : screenshotUrl && String(screenshotUrl).trim() && !hasImageLoadError ? (
                    <div
                        className={styles.screenshotWrapper}
                        onClick={() => setScreenshotModalOpen(true)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setScreenshotModalOpen(true); }}
                    >
                        <img
                            src={screenshotUrl}
                            alt="Step execution screenshot"
                            className={styles.screenshotImage}
                            onError={async () => {
                                if (!screenshotPath || screenshotRetriedRef.current) {
                                    setHasImageLoadError(true);
                                    return;
                                }
                                screenshotRetriedRef.current = true;
                                const { url } = await refreshScreenshotUrl(screenshotPath);
                                if (url) {
                                    setScreenshotUrl(url);
                                    setHasImageLoadError(false);
                                } else {
                                    setHasImageLoadError(true);
                                }
                            }}
                        />
                        {step.elementBounds && (
                            <div
                                className={styles.elementHighlight}
                                style={{
                                    left: `${step.elementBounds.x}px`,
                                    top: `${step.elementBounds.y}px`,
                                    width: `${step.elementBounds.width}px`,
                                    height: `${step.elementBounds.height}px`
                                }}
                            />
                        )}
                    </div>
                ) : hasImageLoadError ? (
                    <ScreenshotPlaceholder variant="unavailable" size="panel" />
                ) : (
                    <ScreenshotPlaceholder variant="pending" size="panel" />
                )}
            </div>

            {/* Tool Output — collapsed by default for supported tools (browser_evaluate, browser_network_requests, browser_console_messages) */}
            <ToolOutput step={step} stepDetails={stepDetails} />

            {/* Execution Details section removed — Status/Duration/Executed-At now live in the StepEditorView header. */}
            <div className={styles.executionInfoSection}>
                {/* Success Message - Additional Info */}
                {effectiveStatus === 'success' && (step.resultMessage || stepDetails?.result?.result?.message) && (
                    <div className={styles.successSection}>
                        <span className={`${styles.successLabel} ${styles.asTypoCaption}`}>
                            ✓ Result
                        </span>
                        <AutomationText
                            as="p"
                            text={step.resultMessage || stepDetails?.result?.result?.message}
                            className={`${styles.successText} ${styles.asTypoBody2}`}
                        />
                    </div>
                )}

                {/* Healing Trace - shown when step was recovered by self-healer */}
                {stepDetails?.result?.selfHealed && (
                    <div className={styles.healSection}>
                        <span className={styles.healLabel}>🔧 Self-Healed</span>
                        {stepDetails.result.healMethod && (
                            <div className={styles.healRow}>
                                <span className={styles.healKey}>Method:</span>
                                <span className={styles.healValue}>{stepDetails.result.healMethod}</span>
                            </div>
                        )}
                        {stepDetails.result.healTool && (
                            <div className={styles.healRow}>
                                <span className={styles.healKey}>Tool:</span>
                                <span className={styles.healValue}>{stepDetails.result.healTool}</span>
                            </div>
                        )}
                        {stepDetails.result.healedRef && (
                            <div className={styles.healRow}>
                                <span className={styles.healKey}>Element ref:</span>
                                <span className={styles.healValue}>{stepDetails.result.healedRef}</span>
                            </div>
                        )}
                        {stepDetails.result.healAttempt != null && (
                            <div className={styles.healRow}>
                                <span className={styles.healKey}>Attempt:</span>
                                <span className={styles.healValue}>{stepDetails.result.healAttempt}</span>
                            </div>
                        )}
                        {stepDetails.result.healArgs && (
                            <div>
                                <span className={styles.healKey}>Arguments:</span>
                                <pre style={{ margin: '4px 0 0', padding: '6px 8px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 4, fontSize: 11, fontFamily: 'monospace', color: '#1e293b', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 160, overflow: 'auto' }}>
                                    {JSON.stringify(stepDetails.result.healArgs, null, 2)}
                                </pre>
                            </div>
                        )}
                        {stepDetails.result.toolCallTrace?.response && (
                            <div>
                                <span className={styles.healKey}>Response:</span>
                                <pre style={{ margin: '4px 0 0', padding: '6px 8px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 4, fontSize: 11, fontFamily: 'monospace', color: '#1e293b', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 80, overflow: 'auto' }}>
                                    {stepDetails.result.toolCallTrace.response}
                                </pre>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {replayTraceTurns?.length > 0 ? (
                <StepAgentResultTrace agentTraceTurns={replayTraceTurns} />
            ) : null}

            {/* Screenshot Modal - shared ImagePreviewModal (same as RecordPage) */}
            <ImagePreviewModal
                open={screenshotModalOpen}
                onClose={() => setScreenshotModalOpen(false)}
                imageUrl={screenshotUrl}
                fileName={format(step.description || step.action) || "Step execution screenshot"}
                onImageError={async () => {
                    if (!screenshotPath) return null;
                    const { url } = await refreshScreenshotUrl(screenshotPath);
                    if (url) {
                        setScreenshotUrl(url);
                        setHasImageLoadError(false);
                        return url;
                    }
                    return null;
                }}
            />
        </div>
    );
}
